import { useEffect } from "react";

/**
 * Per-page SEO manager — sets <title>, meta description, canonical,
 * full Open Graph tags, Twitter Card, and JSON-LD structured data.
 */
function upsertMeta(attr, key, value) {
  if (!value) return;
  let el = document.head.querySelector(`meta[${attr}="${key}"]`);
  if (!el) {
    el = document.createElement("meta");
    el.setAttribute(attr, key);
    document.head.appendChild(el);
  }
  el.setAttribute("content", value);
}

function upsertLink(rel, href) {
  if (!href) return;
  let el = document.head.querySelector(`link[rel="${rel}"]`);
  if (!el) { el = document.createElement("link"); el.setAttribute("rel", rel); document.head.appendChild(el); }
  el.setAttribute("href", href);
}

const DEFAULT_IMAGE = "/icons/icon-512.png";

export default function SEO({
  title,
  description,
  canonical,
  image,
  type = "website",
  locale,
  siteName = "ImageKitHub",
  twitterHandle,
  jsonLd = [],
}) {
  useEffect(() => {
    const url = canonical || window.location.href;
    const imgUrl = image || `${window.location.origin}${DEFAULT_IMAGE}`;
    const currentLocale = locale || (document.documentElement.lang === "hi" ? "hi_IN" : "en_US");

    if (title) document.title = title;

    // Basic
    upsertMeta("name", "description", description);
    upsertMeta("name", "robots", "index,follow,max-image-preview:large");

    // Open Graph
    upsertMeta("property", "og:title", title);
    upsertMeta("property", "og:description", description);
    upsertMeta("property", "og:type", type);
    upsertMeta("property", "og:url", url);
    upsertMeta("property", "og:image", imgUrl);
    upsertMeta("property", "og:image:width", "1200");
    upsertMeta("property", "og:image:height", "630");
    upsertMeta("property", "og:site_name", siteName);
    upsertMeta("property", "og:locale", currentLocale);

    // Twitter Card
    upsertMeta("name", "twitter:card", "summary_large_image");
    upsertMeta("name", "twitter:title", title);
    upsertMeta("name", "twitter:description", description);
    upsertMeta("name", "twitter:image", imgUrl);
    if (twitterHandle) upsertMeta("name", "twitter:site", twitterHandle);

    upsertLink("canonical", url);

    // Per-page JSON-LD
    document.querySelectorAll('script[data-seo-jsonld="true"]').forEach((n) => n.remove());
    jsonLd.forEach((obj) => {
      const s = document.createElement("script");
      s.type = "application/ld+json";
      s.setAttribute("data-seo-jsonld", "true");
      s.text = JSON.stringify(obj);
      document.head.appendChild(s);
    });
  }, [title, description, canonical, image, type, locale, siteName, twitterHandle, JSON.stringify(jsonLd)]);
  return null;
}
